import React, {Fragment} from "react"
import {startCase} from "lodash"
import classNames from "classnames"
import moment from "moment/moment"
import Button from "../../Button/Button"
import {KeyValueCard, KeyValueCardContainer, KeyValueCardKey, KeyValueCardValue} from "../../KeyValueCard/KeyValueCard"
import {ApplicationDocument, ApplicationDocumentStatus, ApplicationDocumentType} from "../../../resources/application"
import {DocumentableApplication} from "../../../resources/documentable"
import {addressText} from "../../../utilities/helpers"
import {useDocumentableIsAllowUpdateDocument} from "../../../services/documentablePermission"

interface DocumentPreviewModeProps {
	onEditButtonClick: () => void
	application: DocumentableApplication
	document: ApplicationDocument
}

const notAvailable = "N.A"

const getKeyValCard = (application: DocumentableApplication, document: ApplicationDocument) => {
	const {name, dateOfBirth, passport, ssn, ein, matriculaConsular, nationality, stateOfIncorporation} =
		document.attributes
	const addressData =
		document.attributes.address || ("address" in application.attributes && application.attributes?.address)
	const industry = "industry" in application.attributes && application.attributes.industry
	const dba = "dba" in application.attributes && application.attributes.dba
	const documentType = document.attributes.documentType

	const birthDate = dateOfBirth ? moment(dateOfBirth).format("MM/DD/YYYY") : notAvailable
	const address = addressData && <span className={"address-placeholder"}> {addressText(addressData)} </span>
	const decisionBy = document.attributes.decisionBy

	let docAttributes: {[key: string]: boolean | string | JSX.Element} = {}

	switch (documentType) {
		case ApplicationDocumentType.addressVerification:
			docAttributes = {
				...(documentType ? {type: startCase(documentType)} : {}),
				...(name ? {name} : {}),
				...(address ? {address} : {}),
				...(decisionBy ? {decisionBy} : {}),
			}
			break

		case ApplicationDocumentType.idDocument:
			docAttributes = {
				...(documentType ? {type: startCase(documentType)} : {}),
				...(name ? {name} : {}),
				...(birthDate ? {birthDate} : {}),
				...(ssn ? {SSN: ssn} : {}),
				...(address ? {address} : {}),
				...(decisionBy ? {decisionBy} : {}),
			}
			break

		case ApplicationDocumentType.selfieVerification:
			docAttributes = {
				...(documentType ? {type: startCase(documentType)} : {}),
				...(name ? {name} : {}),
				...(address ? {address} : {}),
				...(decisionBy ? {decisionBy} : {}),
			}
			break

		case ApplicationDocumentType.passport:
			docAttributes = {
				...(documentType ? {type: startCase(documentType)} : {}),
				...(name ? {name} : {}),
				...(birthDate ? {birthDate} : {}),
				...(passport ? {passport} : {}),
				...(nationality ? {nationality} : {}),
				...(address ? {address} : {}),
				...(decisionBy ? {decisionBy} : {}),
			}
			break

		case ApplicationDocumentType.matriculaConsular:
			docAttributes = {
				...(documentType ? {type: startCase(documentType)} : {}),
				...(name ? {name} : {}),
				...(birthDate ? {birthDate} : {}),
				...(matriculaConsular ? {matriculaConsular} : {}),
				...(address ? {address} : {}),
				...(decisionBy ? {decisionBy} : {}),
			}
			break

		case ApplicationDocumentType.socialSecurityCard:
			docAttributes = {
				...(documentType ? {type: startCase(documentType)} : {}),
				...(name ? {name} : {}),
				...(birthDate ? {birthDate} : {}),
				...(ssn ? {SSN: ssn} : {}),
				...(address ? {address} : {}),
				...(decisionBy ? {decisionBy} : {}),
			}
			break

		case ApplicationDocumentType.certificateOfIncorporation:
		case ApplicationDocumentType.employerIdentificationNumberConfirmation:
			docAttributes = {
				...(documentType ? {type: startCase(documentType)} : {}),
				...(name ? {businessName: name} : {}),
				...(ein ? {EIN: ein} : {}),
				...(stateOfIncorporation ? {stateOfIncorporation} : {}),
				...(industry ? {industry} : {}),
				...(dba ? {DBA: dba} : {}),
				...(decisionBy ? {decisionBy} : {}),
			}
			break

		case ApplicationDocumentType.powerOfAttorney:
			docAttributes = {
				...(documentType ? {type: startCase(documentType)} : {}),
				...(birthDate ? {birthDate} : {}),
				...(address ? {address} : {}),
				...(ssn ? {ssn} : {}),
				...(decisionBy ? {decisionBy} : {}),
			}
			break

		default:
			break
	}

	return docAttributes
}

export function DocumentPreviewMode({onEditButtonClick, application, document}: DocumentPreviewModeProps) {
	const canEdit = !document.attributes.archived && document.attributes.status !== ApplicationDocumentStatus.Approved
	const isAllowUpdateDocument = useDocumentableIsAllowUpdateDocument(application)

	return (
		<>
			<div className={classNames("preview-mode-header-padded")}>
				<div className={classNames("item-flex-start-padded")}>
					<h3 className={classNames("key-value-card-title")}> Document Info </h3>
				</div>
				<div className={classNames("item-flex-end")}>
					{isAllowUpdateDocument && (
						<Button
							disabled={!canEdit}
							theme="neutral"
							variant="flat"
							circular
							prefixIcon="pencil-1--interface-essential"
							onClick={onEditButtonClick}
						/>
					)}
				</div>
			</div>
			<KeyValueCard>
				<KeyValueCardContainer>
					{Object.entries(getKeyValCard(application, document)).map(([key, value]) => (
						<Fragment key={key}>
							<KeyValueCardKey>{startCase(key)}</KeyValueCardKey>
							<KeyValueCardValue>{value}</KeyValueCardValue>
						</Fragment>
					))}
				</KeyValueCardContainer>
			</KeyValueCard>
		</>
	)
}
